import { createSelector } from '@reduxjs/toolkit';

// Base selector for alert slice state.
export const selectAlertListState = (state) => state.alertList; // Access the alert slice state

export const selectAlertList = (state) => selectAlertListState(state).alertList;

export const selectListConfig = (state) => selectAlertListState(state).listConfig;

export const selectPageIndex = (state) => selectListConfig(state).pageIndex;

export const selectSeveritySortDirection = (state) => selectListConfig(state).severitySortDirection;

export const selectShowLocalOnly = (state) => selectListConfig(state).showLocalOnly;

export const selectMaxDist = (state) => selectListConfig(state).maxDist;

export const selectSearchTerm = (state) => selectListConfig(state).searchTerm;

export const selectSelectedAlertConfig = (state) => selectAlertListState(state).selectedAlertConfig;

export const selectAlertId = (state) => selectSelectedAlertConfig(state).alertId;

export const selectMapCenter = (state) => selectSelectedAlertConfig(state).mapCenter;

export const selectMapPolygon = (state) => selectSelectedAlertConfig(state).mapPolygon;

export const selectShowCloseAlertModal = (state) => selectAlertListState(state).showCloseAlertModal;

// Memoized selectors can be added here to derive more complex state from the alert slice
export const selectFilteredAlertList = createSelector(
  [selectAlertList, selectListConfig],
  (alertList, listConfig) => {
    const { searchTerm, showLocalOnly, maxDist, alertTypes = [] } = listConfig;

    return alertList.filter((alert) => {
      // If no search term is provided, all alerts match by default
      const matchesSearchTerm =
        !searchTerm ||
        alert.smpp_message
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(searchTerm?.toLowerCase().replace(/\s/g, '')) ||
        alert.message
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(searchTerm?.toLowerCase().replace(/\s/g, '')) ||
        alert.nws_office
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(searchTerm?.toLowerCase().replace(/\s/g, '')) ||
        alert.description
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(searchTerm?.toLowerCase().replace(/\s/g, ''));

      if (!matchesSearchTerm) return false; // Exclude alerts that do not match the search term

      // exact-type filter (conjunctive with searchTerm): when alertTypes is set, require the alert's canonical type to be one of them
      if (Array.isArray(alertTypes) && alertTypes.length > 0) {
        const typeName = typeof alert.description === 'string' ? alert.description : '';
        if (!typeName || !alertTypes.includes(typeName)) return false;
      }

      if (showLocalOnly) return alert.distanceToUser <= maxDist; // Only include alerts within maxDist if showLocalOnly is true

      return true; // Include all alerts that pass the search term and type checks
    });
  },
);

export const selectSortedAlertList = createSelector(
  [selectFilteredAlertList, selectListConfig],
  (filteredAlertList, listConfig) => {
    const { severitySortDirection } = listConfig;
    const sortedList = [...filteredAlertList];

    if (severitySortDirection !== null) {
      sortedList.sort((a, b) => {
        if (severitySortDirection) return b.severity - a.severity;
        else return a.severity - b.severity;
      });
    }
    return sortedList;
  },
);

export const selectSortedPagedAlertList = createSelector(
  [selectSortedAlertList, selectListConfig],
  (sortedAlertList, listConfig) => {
    const { pageIndex } = listConfig;
    const alertLimit = 100;
    const pageStart = alertLimit * (pageIndex - 1);
    const pageEnd = pageStart + alertLimit;

    return sortedAlertList.slice(pageStart, pageEnd);
  },
);
